import React, { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { ArrowLeft, CalendarDays, CheckCircle2, Clock3, Loader2, ShieldCheck } from 'lucide-react'
import TurnstileGate from '../../../shared/components/TurnstileGate.jsx'

const H = { 'Content-Type': 'application/json', 'x-tenant-id': 'prover' }

async function api(path, options = {}) {
  const res = await fetch(`/api${path}`, { ...options, headers: { ...H, ...(options.headers || {}) } })
  const body = await res.json().catch(() => ({}))
  if (!res.ok) {
    const err = new Error(body.error || `Error ${res.status}`)
    err.code = body.code || ''
    err.status = res.status
    throw err
  }
  return body
}

function formatPhone(value) {
  return String(value || '').replace(/[^0-9+]/g, '').slice(0, 16)
}

function isMinorBirthdate(value) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(value || ''))) return false
  const dob = new Date(`${value}T12:00:00Z`)
  if (Number.isNaN(dob.getTime())) return false
  const now = new Date()
  let age = now.getUTCFullYear() - dob.getUTCFullYear()
  const beforeBirthday =
    now.getUTCMonth() < dob.getUTCMonth()
    || (now.getUTCMonth() === dob.getUTCMonth() && now.getUTCDate() < dob.getUTCDate())
  if (beforeBirthday) age -= 1
  return age < 18
}

export default function ProverBookingRelease() {
  const [providers, setProviders] = useState([])
  const [providerId, setProviderId] = useState('')
  const [date, setDate] = useState('')
  const [slots, setSlots] = useState([])
  const [time, setTime] = useState('')
  const [patient, setPatient] = useState({ name: '', phone: '', dni: '', birthdate: '' })
  const [otp, setOtp] = useState('')
  const [otpSent, setOtpSent] = useState(false)
  const [otpVerified, setOtpVerified] = useState(false)
  const [turnstileToken, setTurnstileToken] = useState('')
  const [privacyConsent, setPrivacyConsent] = useState({
    accepted: false,
    guardianConfirmed: false,
    guardianRelationship: ''
  })
  const [loading, setLoading] = useState(false)
  const [loadingSlots, setLoadingSlots] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(null)

  const minDate = useMemo(() => new Date().toISOString().slice(0, 10), [])
  const maxDate = useMemo(() => {
    const d = new Date()
    d.setDate(d.getDate() + 30)
    return d.toISOString().slice(0, 10)
  }, [])

  useEffect(() => {
    document.title = 'Agendar consulta | ProVer'
    let active = true
    api('/providers')
      .then((body) => { if (active) setProviders(body.data || body.providers || []) })
      .catch((e) => { if (active) setError(e.message || 'No pudimos cargar los profesionales.') })
    return () => { active = false }
  }, [])

  useEffect(() => {
    if (!providerId || !date) { setSlots([]); setTime(''); return }
    let active = true
    setLoadingSlots(true)
    setError('')
    api(`/availability?provider_id=${encodeURIComponent(providerId)}&date=${encodeURIComponent(date)}`)
      .then((body) => {
        if (!active) return
        setSlots((body.slots || []).filter((s) => s.available !== false))
        setTime('')
      })
      .catch((e) => { if (active) setError(e.message || 'No pudimos consultar la disponibilidad.') })
      .finally(() => { if (active) setLoadingSlots(false) })
    return () => { active = false }
  }, [providerId, date])

  const selectedProvider = providers.find((p) => String(p.id || p.provider_id) === String(providerId))

  const validateIdentity = () => {
    if (!patient.name.trim()) return 'Ingresá nombre y apellido.'
    if (!patient.phone.trim()) return 'Ingresá un número de WhatsApp.'
    if (!patient.dni.trim()) return 'Ingresá la cédula del paciente.'
    if (!patient.birthdate) return 'Ingresá la fecha de nacimiento.'
    return ''
  }

  async function requestOtp() {
    const v = validateIdentity()
    if (v) { setError(v); return }
    if (!privacyConsent.accepted) {
      setError('Aceptá la Política de Privacidad y los Términos de Uso para continuar.')
      return
    }
    if (isMinorBirthdate(patient.birthdate)
      && (!privacyConsent.guardianConfirmed || !privacyConsent.guardianRelationship)) {
      setError('Para un paciente menor, confirmá el vínculo del responsable autorizado.')
      return
    }
    setLoading(true); setError('')
    try {
      await api('/otp/request', {
        method: 'POST',
        body: JSON.stringify({ action: 'request', phone: patient.phone })
      })
      setOtpSent(true)
      setOtpVerified(false)
    } catch (e) {
      setError(e.message || 'No pudimos enviar el código por WhatsApp.')
    } finally { setLoading(false) }
  }

  async function verifyOtp() {
    if (!otp || otp.replace(/\D/g, '').length !== 6) { setError('Ingresá el código de 6 dígitos.'); return }
    setLoading(true); setError('')
    try {
      await api('/otp/verify', {
        method: 'POST',
        body: JSON.stringify({ action: 'verify', phone: patient.phone, code: otp.replace(/\D/g, '') })
      })
      setOtpVerified(true)
    } catch (e) {
      setOtpVerified(false)
      setError(e.message || 'El código es incorrecto o venció.')
    } finally { setLoading(false) }
  }

  async function confirmAppointment() {
    setError('')
    if (!providerId || !date || !time) { setError('Seleccioná profesional, fecha y horario.'); return }
    const v = validateIdentity(); if (v) { setError(v); return }
    if (!privacyConsent.accepted) { setError('Aceptá la Política de Privacidad y los Términos de Uso para continuar.'); return }
    if (isMinorBirthdate(patient.birthdate)
      && (!privacyConsent.guardianConfirmed || !privacyConsent.guardianRelationship)) {
      setError('Para un paciente menor, confirmá el vínculo del responsable autorizado.')
      return
    }
    if (!otpVerified) { setError('Verificá tu WhatsApp antes de confirmar.'); return }
    if (import.meta.env.VITE_TURNSTILE_SITE_KEY && !turnstileToken) { setError('Completá la verificación anti-bot.'); return }

    setLoading(true)
    try {
      const preferred = `${date}T${String(time).slice(0, 5)}`
      const body = await api('/appointments', {
        method: 'POST',
        body: JSON.stringify({
          provider_id: providerId,
          preferred_datetime: preferred,
          patient_name: patient.name.trim(),
          patient_phone: patient.phone.trim(),
          patient_dni: patient.dni.replace(/\D/g, ''),
          birthdate: patient.birthdate,
          privacy_consent: privacyConsent.accepted,
          consent_version: 'prover-privacy-2026-09-28-v1',
          guardian_confirmed: privacyConsent.guardianConfirmed,
          guardian_relationship: privacyConsent.guardianRelationship || undefined,
          turnstile_token: turnstileToken || undefined,
          booking_channel: 'web',
          source: 'prover.com.py'
        })
      })
      setSuccess({ appointment: body.appointment || body.data || {}, preferred })
    } catch (e) {
      if (e.code === 'OTP_NOT_VERIFIED') setOtpVerified(false)
      if (e.code === 'SLOT_UNAVAILABLE' || e.status === 409) {
        setTime('')
        setError('Ese horario acaba de ocuparse. Elegí otro disponible.')
      } else {
        setError(e.message || 'No pudimos registrar el turno.')
      }
    } finally { setLoading(false) }
  }

  if (success) {
    return (
      <main className="prover-site prover-subpage" style={{ minHeight: '100vh' }}>
        <section className="portal-panel glass-panel" style={{ maxWidth: 720, margin: '72px auto', padding: 36, textAlign: 'center' }}>
          <CheckCircle2 size={54} style={{ color: '#b66cff', marginBottom: 16 }} />
          <h1>Turno registrado</h1>
          <p>Tu solicitud fue recibida correctamente por ProVer.</p>
          <p><strong>{selectedProvider?.name || selectedProvider?.full_name || 'Profesional seleccionado'}</strong><br />{date} · {String(time).slice(0, 5)}</p>
          <Link className="primary-button" to="/">Volver a ProVer</Link>
        </section>
      </main>
    )
  }

  return (
    <main className="prover-site prover-subpage" style={{ minHeight: '100vh' }}>
      <div className="prover-orb prover-orb-a" /><div className="prover-orb prover-orb-b" />
      <header className="prover-nav glass-panel">
        <Link to="/" className="prover-brand"><span style={{ fontWeight: 800, letterSpacing: '.04em' }}>PROVER</span></Link>
        <Link className="nav-pill" to="/"><ArrowLeft size={17} /> Volver</Link>
      </header>

      <section className="prover-hero compact">
        <div className="hero-copy">
          <span className="eyebrow">AGENDAMIENTO SEGURO</span>
          <h1>Reservá tu consulta</h1>
          <p>Elegí profesional y horario. Antes de confirmar verificamos tu WhatsApp para proteger tus datos y evitar reservas falsas.</p>
        </div>
      </section>

      <section className="portal-panel glass-panel" style={{ maxWidth: 920, margin: '0 auto 80px' }}>
        {error && <div className="error-toast glass-panel" role="alert">{error}</div>}

        <div className="portal-form">
          <label>Profesional</label>
          <select value={providerId} onChange={(e) => { setProviderId(e.target.value); setOtpVerified(false) }}>
            <option value="">Seleccionar profesional</option>
            {providers.map((p) => <option key={p.id || p.provider_id} value={p.id || p.provider_id}>{p.name || p.full_name} {p.specialty ? `— ${p.specialty}` : ''}</option>)}
          </select>

          <label>Fecha</label>
          <div className="input-wrap"><CalendarDays size={18} /><input type="date" min={minDate} max={maxDate} value={date} onChange={(e) => setDate(e.target.value)} /></div>

          <label>Horario disponible</label>
          {loadingSlots ? <div className="centered"><Loader2 className="spin" /> Consultando agenda…</div> : (
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 18 }}>
              {slots.map((s) => <button type="button" key={s.time} onClick={() => setTime(s.time)} className={time === s.time ? 'primary-button' : 'nav-pill'}><Clock3 size={15} /> {String(s.time).slice(0,5)}</button>)}
              {providerId && date && slots.length === 0 && <span className="muted">No hay horarios disponibles para esa fecha.</span>}
            </div>
          )}

          <label>Nombre y apellido</label>
          <input value={patient.name} onChange={(e) => { setPatient({ ...patient, name: e.target.value }); setOtpVerified(false) }} autoComplete="name" />

          <label>Cédula</label>
          <input inputMode="numeric" value={patient.dni} onChange={(e) => { setPatient({ ...patient, dni: e.target.value.replace(/\D/g, '').slice(0, 12) }); setOtpVerified(false) }} />

          <label>Fecha de nacimiento</label>
          <input type="date" max={minDate} value={patient.birthdate} onChange={(e) => { setPatient({ ...patient, birthdate: e.target.value }); setOtpVerified(false) }} />

          <label>WhatsApp</label>
          <input inputMode="tel" value={patient.phone} onChange={(e) => { setPatient({ ...patient, phone: formatPhone(e.target.value) }); setOtpSent(false); setOtpVerified(false); setOtp('') }} placeholder="0981 123 456" autoComplete="tel" />

          <div className="glass-panel" style={{ padding: 16, marginTop: 14 }}>
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={privacyConsent.accepted}
                onChange={(e) => setPrivacyConsent((p) => ({ ...p, accepted: e.target.checked }))}
                style={{ marginTop: 4 }}
              />
              <span className="hint" style={{ margin: 0 }}>
                Confirmo que los datos son correctos y acepto los <Link to="/terminos" target="_blank">Términos de Uso</Link> y la <Link to="/privacidad" target="_blank">Política de Privacidad</Link> para gestionar este turno.
              </span>
            </label>

            {isMinorBirthdate(patient.birthdate) && (
              <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid rgba(255,255,255,.12)' }}>
                <label>Vínculo con el paciente menor</label>
                <select
                  value={privacyConsent.guardianRelationship}
                  onChange={(e) => setPrivacyConsent((p) => ({ ...p, guardianRelationship: e.target.value }))}
                >
                  <option value="">Seleccionar vínculo</option>
                  <option value="madre">Madre</option>
                  <option value="padre">Padre</option>
                  <option value="tutor">Tutor/a legal</option>
                  <option value="responsable">Responsable autorizado</option>
                  <option value="otro_autorizado">Otro autorizado</option>
                </select>
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer', marginTop: 10 }}>
                  <input
                    type="checkbox"
                    checked={privacyConsent.guardianConfirmed}
                    onChange={(e) => setPrivacyConsent((p) => ({ ...p, guardianConfirmed: e.target.checked }))}
                    style={{ marginTop: 4 }}
                  />
                  <span className="hint" style={{ margin: 0 }}>
                    Declaro que soy padre, madre, tutor o responsable autorizado para gestionar este turno.
                  </span>
                </label>
              </div>
            )}
          </div>

          {!otpSent && <button type="button" className="primary-button" disabled={loading} onClick={requestOtp}>{loading ? <><Loader2 className="spin" /> Enviando…</> : <><ShieldCheck size={18} /> Verificar WhatsApp</>}</button>}

          {otpSent && !otpVerified && (
            <div className="glass-panel" style={{ padding: 18, marginTop: 12 }}>
              <label>Código recibido por WhatsApp</label>
              <input value={otp} inputMode="numeric" maxLength={6} onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="000000" />
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 10 }}>
                <button type="button" className="primary-button" disabled={loading} onClick={verifyOtp}>{loading ? <><Loader2 className="spin" /> Verificando…</> : 'Validar código'}</button>
                <button type="button" className="text-button" disabled={loading} onClick={requestOtp}>Reenviar</button>
              </div>
            </div>
          )}

          {otpVerified && <div className="notice success" style={{ marginTop: 12 }}><CheckCircle2 size={18} /> WhatsApp verificado.</div>}

          {!!import.meta.env.VITE_TURNSTILE_SITE_KEY && <div style={{ marginTop: 18 }}><TurnstileGate onVerify={setTurnstileToken} /></div>}

          <button type="button" className="primary-button" style={{ marginTop: 22, width: '100%' }} disabled={loading || !otpVerified || !time} onClick={confirmAppointment}>
            {loading ? <><Loader2 className="spin" /> Confirmando…</> : 'Confirmar turno'}
          </button>
          <p className="hint">El consentimiento queda registrado con la versión vigente de la política y la fecha/hora de la reserva.</p>
        </div>
      </section>
    </main>
  )
}
